import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  Layers,
  ChevronDown,
  BarChart3,
  Users,
  MessageSquare,
  DollarSign,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import Logo from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = ({ children }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    total_services: 0,
    total_packages: 0,
    active_services: 0,
    active_packages: 0,
  });

  const adminName = localStorage.getItem('admin_name') || 'Admin';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
        }
      }
    };

    fetchStats();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t('admin_dashboard') },
    { path: '/admin/services', icon: Layers, label: t('admin_services') },
    { path: '/admin/packages', icon: Package, label: t('admin_packages') },
    { path: '/admin/payroll', icon: DollarSign, label: language === 'ar' ? 'الرواتب' : 'Payroll' },
    { path: '/admin/settings', icon: Settings, label: t('admin_settings') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 start-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
          <Logo size="md" showText={true} lightText={false} />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive(item.path)
                    ? 'bg-emerald-50 text-emerald-600 font-medium'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <IconComponent className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Stats */}
        <div className="absolute bottom-20 start-0 end-0 p-4">
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{t('admin_active_services')}</span>
              <span className="font-bold text-emerald-600">{stats.active_services}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{t('admin_active_packages')}</span>
              <span className="font-bold text-emerald-600">{stats.active_packages}</span>
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="absolute bottom-0 start-0 end-0 p-4 border-t border-slate-100">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 me-2" />
            {t('admin_logout')}
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ms-64">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold text-slate-900">
              {language === 'ar' ? `مرحباً، ${adminName}` : `Welcome, ${adminName}`}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{language === 'ar' ? 'العربية' : 'English'}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => language !== 'ar' && toggleLanguage()}>
                  العربية
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => language !== 'en' && toggleLanguage()}>
                  English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Back to site */}
            <Link to="/">
              <Button variant="outline" size="sm" className="rounded-full">
                {language === 'ar' ? 'عرض الموقع' : 'View Site'}
              </Button>
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
